import { encode } from "uqr";

/**
 * QR codes for UPI payment links, for the staff app's full-screen QR and the
 * storefront's "Pay with UPI" fallback. Also exported alone as
 * `@luckymenu/billing-core/qr`, so browser code gets it without the zod
 * schemas of the main entry. Printed bills do not use this: printers
 * draw QR codes with their own ESC/POS command (printing.md).
 *
 * Error correction level M, and a quiet zone of four modules as the QR
 * standard asks, so the code scans from a screen at arm's length.
 */

export interface QrMatrix {
	/** Modules per side, quiet zone included. */
	size: number;
	/** `modules[row][column]`, true for a dark module. */
	modules: boolean[][];
}

export const QR_QUIET_ZONE = 4;

export function qrMatrix(text: string): QrMatrix {
	const result = encode(text, { ecc: "M", border: QR_QUIET_ZONE });
	return { size: result.size, modules: result.data };
}

/**
 * The dark modules as one SVG path, one unit per module: dark runs in a row
 * are merged into one rectangle to keep it small. React surfaces draw it in
 * an `<svg viewBox="0 0 size size">`; `qrSvg` wraps it as a string.
 */
export function qrPath(text: string): { size: number; d: string } {
	const { size, modules } = qrMatrix(text);
	let d = "";
	modules.forEach((row, y) => {
		let x = 0;
		while (x < size) {
			if (!row[x]) {
				x++;
				continue;
			}
			const start = x;
			while (x < size && row[x]) x++;
			d += `M${start} ${y}h${x - start}v1h-${x - start}z`;
		}
	});
	return { size, d };
}

/**
 * The QR code as an SVG string that scales to its container, on a white
 * square. React Native renders `qrMatrix` itself; web surfaces can use this
 * directly, or `qrPath` inside their own `<svg>`.
 */
export function qrSvg(text: string): string {
	const { size, d } = qrPath(text);
	return (
		`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">` +
		`<rect width="${size}" height="${size}" fill="#fff"/>` +
		`<path d="${d}" fill="#000"/></svg>`
	);
}
